// src/composables/useFocusTrap.ts
import { onMounted, onUnmounted, watch, ref, type Ref } from 'vue';

type FocusTarget = HTMLElement | null;
type MaybeRefEl = Ref<FocusTarget> | FocusTarget;

export interface FocusTrapOptions {
  /** Reactive on/off. If omitted, you control it manually with activate()/deactivate(). */
  active?: Ref<boolean>;
  /** Element/selector to focus on activation. Falls back to the first tabbable element. */
  initialFocus?: HTMLElement | string | null;
  /** Restore focus to the element that was active before the trap (default: true). */
  restoreFocus?: boolean;
  /** Close when the user presses Escape (default: true). */
  escapeDeactivates?: boolean;
  /** Called when Escape is pressed (after deactivation). */
  onEscape?: () => void;
  /** Called when the trap is deactivated. */
  onDeactivate?: () => void;
}

const FOCUSABLE_SELECTOR =
  [
    'a[href]:not([tabindex="-1"])',
    'area[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'iframe:not([tabindex="-1"])',
    'audio[controls]:not([tabindex="-1"])',
    'video[controls]:not([tabindex="-1"])',
    '[contenteditable="true"]:not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

function isVisible(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none';
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return nodes.filter(el => isVisible(el) && !el.hasAttribute('inert'));
}

export function useFocusTrap(target: MaybeRefEl, opts: FocusTrapOptions = {}) {
  const isActive = ref(false);
  let lastFocusedBeforeTrap: Element | null = null;

  const options: Required<Pick<FocusTrapOptions, 'restoreFocus' | 'escapeDeactivates'>> & FocusTrapOptions = {
    restoreFocus: opts.restoreFocus ?? true,
    escapeDeactivates: opts.escapeDeactivates ?? true,
    ...opts
  };

  const resolveTarget = (): HTMLElement | null => {
    return (typeof (target as any).value !== 'undefined'
      ? (target as Ref<HTMLElement | null>).value
      : (target as HTMLElement)) ?? null;
  };

  const tryFocus = (el: HTMLElement | null | undefined) => {
    if (!el) return false;
    if (!isVisible(el) || el.hasAttribute('inert') || el.matches(':disabled')) {
      return false;
    }
    if (typeof (el as any).focus === 'function') {
      el.focus({ preventScroll: true });
      const activeElement = document.activeElement;
      return activeElement === el || (activeElement instanceof Node && el.contains(activeElement));
    }
    return false;
  };

  const focusInitial = (container: HTMLElement) => {
    // 1) User-defined initialFocus
    if (options.initialFocus) {
      if (typeof options.initialFocus === 'string') {
        const el = container.querySelector<HTMLElement>(options.initialFocus);
        if (el && tryFocus(el)) return;
      } else if (options.initialFocus instanceof HTMLElement) {
        if (tryFocus(options.initialFocus)) return;
      }
    }
    // 2) First focusable element
    const focusables = getFocusable(container);
    if (focusables.length) {
      tryFocus(focusables[0]);
      return;
    }
    // 3) Container itself (requires tabindex)
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }
    tryFocus(container);
  };

  const onKeydown = (e: KeyboardEvent) => {
    if (!isActive.value) return;
    const container = resolveTarget();
    if (!container) return;

    if (e.key === 'Escape' && options.escapeDeactivates) {
      e.stopPropagation();
      e.preventDefault();
      deactivate();
      options.onEscape?.();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusables = getFocusable(container);
    if (focusables.length === 0) {
      // No focusable elements: keep focus on the container
      e.preventDefault();
      tryFocus(container);
      return;
    }

    const current = document.activeElement as HTMLElement | null;
    const currentIndex = current ? focusables.indexOf(current) : -1;
    const goingBackwards = e.shiftKey;

    if (goingBackwards) {
      // Shift+Tab
      if (currentIndex <= 0) {
        e.preventDefault();
        tryFocus(focusables[focusables.length - 1]);
      }
    } else {
      // Tab
      if (currentIndex === -1 || currentIndex === focusables.length - 1) {
        e.preventDefault();
        tryFocus(focusables[0]);
      }
    }
  };

  const onFocusin = (e: FocusEvent) => {
    if (!isActive.value) return;
    const container = resolveTarget();
    if (!container) return;

    if (e.target instanceof Node && !container.contains(e.target)) {
      // Move focus back if something tries to focus outside the trap
      const focusables = getFocusable(container);
      (focusables[0] ?? container).focus({ preventScroll: true });
    }
  };

  const activate = () => {
    if (isActive.value) return;
    const container = resolveTarget();
    if (!container) return;

    isActive.value = true;
    lastFocusedBeforeTrap = document.activeElement;

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('focusin', onFocusin, true);

    // Mark dialog behavior when relevant (helps screen readers)
    if (!container.hasAttribute('role')) {
      container.setAttribute('role', 'dialog');
    }
    if (!container.hasAttribute('aria-modal')) {
      container.setAttribute('aria-modal', 'true');
    }

    // Initialize focus
    focusInitial(container);
  };

  const deactivate = () => {
    if (!isActive.value) return;
    isActive.value = false;

    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('focusin', onFocusin, true);

    if (options.restoreFocus && lastFocusedBeforeTrap instanceof HTMLElement) {
      lastFocusedBeforeTrap.focus({ preventScroll: true });
    }
    options.onDeactivate?.();
  };

  // Connect to reactive "active" when provided
  onMounted(() => {
    if (options.active) {
      watch(options.active, (val) => {
        if (val) activate();
        else deactivate();
      }, { immediate: true });
    }
  });

  onUnmounted(() => {
    deactivate();
  });

  return { activate, deactivate, isActive };
}
